/**
 * `sceneItemToLayer` — converts a `SceneItem` from a gTwin scene slot into
 * a viewer `Layer`.
 *
 * Reuses the COG/TiTiler tile-template logic from `artifactToLayer.ts`
 * (`buildCogTileTemplate` extracted here via the same pattern) for
 * `layerKind === "raster"` items with a `cog` endpoint.
 *
 * D6 compliance: `engineeringModels` items get a non-blocking placeholder
 * (`visibility.engineeringPlaceholder = true`) — the caller renders a
 * descriptive note in the LayerPanel; this function never throws on them.
 */

import type { Layer, LayerKind } from '@geosoft/viewer-sdk';
import {
  ARTIFACT_SCENE_CONTRACT,
  isViewerUnsupportedBinaryVectorKind,
  VIEWER_UNSUPPORTED_BINARY_VECTOR_MESSAGE,
  type ArtifactSceneContract,
  type SceneItem,
  type SceneSlot,
} from '@/sdk-stub/gtwin-sdk';
import { resolveActivation } from '@/sdk-stub/viewer-sdk/policy/activationPolicy';
import { rewriteConverterFetchUrl, rewriteBrowserAssetUrl } from '@/utils/titilerSourceHost';
import { titilerBidxQuery, isDemLikeRaster } from '@/utils/titilerRasterBands';
import { applyRasterColorParams } from '@/utils/titilerDemColorize';
import { resolveTerrainDerivativeRole } from '@/components/terrain/terrainDerivedLayers';
import { applyStoredGeoreferenceToLayer } from '@/components/georeference/needsGeoreference';

const SCENE_SLOT_TO_LAYER_KIND: Readonly<Record<SceneSlot, LayerKind>> = {
  basemaps: 'raster',
  terrain: 'terrain',
  realityData: 'raster',
  engineeringModels: 'bim',
  featureLayers: 'vector',
  overlays: 'vector',
  environment: 'environment',
};

function buildCogTileTemplate(cogUrl: string, item?: SceneItem): string {
  const tilesBase = (
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TILES_URL) ||
    'http://localhost:8000'
  ).replace(/\/+$/, '');
  const artifactKind = item ? readArtifactKind(item) : undefined;
  const bandHint = {
    id: item?.id,
    title: item
      ? String((item as unknown as { title?: unknown }).title ?? item.style?.title ?? item.id)
      : undefined,
    family: item ? String(item.style?.family ?? '') : undefined,
    artifactKind,
    terrainDerivative: item ? String(item.style?.terrainDerivative ?? '') : undefined,
  };
  const bidxQuery = titilerBidxQuery(bandHint);
  const fetchable = rewriteConverterFetchUrl(cogUrl);
  let template =
    `${tilesBase}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodeURIComponent(fetchable)}${bidxQuery}`;
  // Bake the QGIS-like stretch (or the derivative's role colormap + masked
  // nodata) into the layer URL at build time so the first engine mount cannot
  // paint raw float JPEG/PNG static, nor an opaque nodata rectangle (hide→show
  // used to "fix" this only because remount hit a warm stats cache).
  if (isDemLikeRaster(bandHint)) {
    template = applyRasterColorParams(template, {
      id: item?.id ?? '',
      kind: 'raster',
      label: bandHint.title,
      style: item?.style,
      endpoints: {},
    } as Parameters<typeof applyRasterColorParams>[1]);
  }
  return template;
}

function buildTerrainCogTileTemplate(cogUrl: string): string {
  const tilesBase = (
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TILES_URL) ||
    'http://localhost:8000'
  ).replace(/\/+$/, '');
  const fetchable = rewriteConverterFetchUrl(cogUrl);
  return `${tilesBase}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodeURIComponent(fetchable)}&bidx=1`;
}

function toViewerLayerKind(layerKind: string, slot: SceneSlot): LayerKind {
  if (layerKind === 'sensor/timeseries') return 'timeseries';
  if (layerKind === 'panorama-bubble') return 'panorama-bubble';
  // G30 — the terrain slot is authoritative: converter registrations may
  // spell the artifact as `cog`, `dem`, `terrain-cog`, etc. The viewer
  // engine and terrain tool card key on `kind === "terrain"`, not layer id.
  if (slot === 'terrain') return 'terrain';
  // XYZ/WMTS raster basemaps arrive with various source-kind spellings; the
  // viewer engine adapters only know the canonical 'raster' kind. Normalise so
  // basemaps mount instead of throwing "No adapter supports layer kind".
  if (layerKind === 'raster-xyz' || layerKind === 'xyz' || layerKind === 'wmts') {
    return 'raster';
  }
  return (layerKind || SCENE_SLOT_TO_LAYER_KIND[slot]) as LayerKind;
}

function readArtifactKind(item: SceneItem): string | undefined {
  if (typeof (item as unknown as { artifactKind?: unknown }).artifactKind === 'string') {
    return (item as unknown as { artifactKind: string }).artifactKind;
  }
  if (typeof item.style?.artifactKind === 'string') return item.style.artifactKind;
  if (typeof item.style?.gtwinArtifactKind === 'string') return item.style.gtwinArtifactKind;
  return undefined;
}

function contractCandidates(item: SceneItem, slot: SceneSlot): ArtifactSceneContract[] {
  const artifactKind = readArtifactKind(item);
  if (isViewerUnsupportedBinaryVectorKind(artifactKind)) {
    return [];
  }
  return Object.values(ARTIFACT_SCENE_CONTRACT).filter((contract) => {
    if (artifactKind && contract.artifactKind !== artifactKind) {
      // G26 — producers spell the same XYZ basemap artifact as `raster-xyz`.
      if (!(artifactKind === 'raster-xyz' && contract.artifactKind === 'xyz')) {
        return false;
      }
    }
    if (contract.layerKind !== item.layerKind) {
      // G26 — basemap XYZ items may use layerKind xyz | raster-xyz | raster | wmts.
      const xyzLayerKinds = new Set(['xyz', 'raster-xyz', 'raster', 'wmts']);
      if (
        !(
          slot === 'basemaps'
          && contract.artifactKind === 'xyz'
          && xyzLayerKinds.has(item.layerKind)
        )
      ) {
        return false;
      }
    }
    // E57 panorama-bubble gate projects register in `realityData`; the
    // canonical contract slot is `overlays` — accept either slot.
    if (
      contract.artifactKind === 'panorama-bubble-v1'
      && (slot === 'realityData' || slot === 'overlays')
    ) {
      return true;
    }
    return contract.slot === slot;
  });
}

function endpointFromContract(
  item: SceneItem,
  slot: SceneSlot,
): { contract: ArtifactSceneContract; key: string; value: string } | null {
  for (const contract of contractCandidates(item, slot)) {
    const keys = [contract.endpointKey, ...(contract.endpointAliases ?? [])];
    for (const key of keys) {
      const value = endpointHref(item.endpoints[key]);
      if (value) return { contract, key, value };
    }
  }
  return null;
}

function endpointHref(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0) return value;
  if (
    typeof value === 'object'
    && value !== null
    && typeof (value as { href?: unknown }).href === 'string'
    && (value as { href: string }).href.length > 0
  ) {
    return (value as { href: string }).href;
  }
  return undefined;
}

function assignEndpoint(
  endpoints: Layer['endpoints'],
  endpointKey: string,
  value: string,
  layerKind: LayerKind,
  item: SceneItem,
): void {
  switch (endpointKey) {
    case 'cog':
      if (layerKind === 'terrain') {
        endpoints.terrain = buildTerrainCogTileTemplate(value);
      } else {
        endpoints.tiles = buildCogTileTemplate(value, item);
      }
      break;
    case 'tiles':
      // Absolute XYZ templates (non-TiTiler) may still be browser-fetched.
      endpoints.tiles = value.includes('{z}') ? value : rewriteBrowserAssetUrl(value);
      break;
    case 'pmtiles':
      endpoints.pmtiles = rewriteBrowserAssetUrl(value);
      break;
    case 'features':
      endpoints.features = rewriteBrowserAssetUrl(value);
      break;
    case 'tileset3d':
      endpoints.tileset3d = rewriteBrowserAssetUrl(value);
      break;
    case 'terrain':
      if (/\.(?:tif|tiff)(?:[?#]|$)/i.test(value)) {
        endpoints.terrain = buildTerrainCogTileTemplate(value);
      } else {
        endpoints.terrain = rewriteBrowserAssetUrl(value);
      }
      break;
    case 'model':
      endpoints.model = rewriteBrowserAssetUrl(value);
      if (layerKind === 'panorama') endpoints.manifest = rewriteBrowserAssetUrl(value);
      break;
    case 'potree':
      endpoints.potree = rewriteBrowserAssetUrl(value);
      break;
    case 'copc':
      endpoints.copc = rewriteBrowserAssetUrl(value);
      break;
    case 'pointcloud':
      if (isCopcUrl(value)) endpoints.copc = rewriteBrowserAssetUrl(value);
      else if (isPotreeMetadataUrl(value)) endpoints.potree = rewriteBrowserAssetUrl(value);
      else endpoints.pointcloud = rewriteBrowserAssetUrl(value);
      break;
    case 'manifest':
      endpoints.manifest = rewriteBrowserAssetUrl(value);
      break;
    case 'stations':
      endpoints.stations = rewriteBrowserAssetUrl(value);
      break;
    case 'stream':
      endpoints.stream = rewriteBrowserAssetUrl(value);
      break;
    default:
      break;
  }
}

function hasExplicitBbox(item: SceneItem): boolean {
  return Array.isArray(item.bbox) && item.bbox.length >= 4;
}

function readCoordinateMode(item: SceneItem): string | undefined {
  const raw = item as unknown as { coordinateMode?: unknown };
  if (typeof raw.coordinateMode === 'string' && raw.coordinateMode.length > 0) return raw.coordinateMode;
  if (typeof item.style?.coordinateMode === 'string' && item.style.coordinateMode.length > 0) {
    return item.style.coordinateMode;
  }
  return undefined;
}

function inferCoordinateMode(item: SceneItem): string {
  const explicit = readCoordinateMode(item);
  if (explicit) return explicit;
  if (!item.crs && !hasExplicitBbox(item)) return 'unknown-local';
  if (!item.crs) return 'local-engineering';
  return 'georeferenced';
}

function isCopcUrl(value: string | undefined): value is string {
  return typeof value === 'string' && /\.copc(?:\.laz)?(?:[?#].*)?$/i.test(value);
}

function isPotreeMetadataUrl(value: string | undefined): value is string {
  return typeof value === 'string' && /(?:^|\/)metadata\.json(?:[?#].*)?$/i.test(value);
}

function rasterWorkflowCapabilities(item: SceneItem, layerKind: LayerKind): Record<string, unknown> | undefined {
  if (layerKind !== 'raster' && layerKind !== 'terrain') return undefined;
  const analysis = item.style?.rasterTerrainAnalysis;
  const hooks =
    typeof analysis === 'object' && analysis !== null
      ? (analysis as { workflowHooks?: unknown }).workflowHooks
      : undefined;
  return {
    opacity: true,
    colorRamps: true,
    hillshade: layerKind === 'terrain',
    blending: true,
    masks: true,
    profileExtraction: layerKind === 'terrain',
    ...(typeof hooks === 'object' && hooks !== null ? hooks : {}),
  };
}

function pointcloudWorkflowCapabilities(item: SceneItem, layerKind: LayerKind): Record<string, unknown> | undefined {
  if (layerKind !== 'pointcloud') return undefined;
  const metadata = item.style?.pointcloud;
  const hooks =
    typeof metadata === 'object' && metadata !== null
      ? (metadata as { workflowHooks?: unknown }).workflowHooks
      : undefined;
  return {
    classificationFiltering: true,
    colorRamps: true,
    pointBudget: true,
    opacity: true,
    rasterColorProjection:
      typeof metadata === 'object'
      && metadata !== null
      && (metadata as { rasterColorProjectionReady?: unknown }).rasterColorProjectionReady === true,
    ...(typeof hooks === 'object' && hooks !== null ? hooks : {}),
  };
}

/**
 * Converter packages in the wild often carry only `artifactKind:
 * 'terrain-<role>-cog'` and no `style.terrainDerivative` tag. Stamp the
 * canonical tag before anything reads it: the tile template needs it to pick
 * the role colormap + nodata mask, and the activation policy below needs it to
 * keep companions `on-demand` instead of mounting four exactly-overlapping
 * COGs, all default-visible.
 */
function withTerrainDerivativeRole(item: SceneItem): SceneItem {
  const tagged = item.style?.terrainDerivative;
  if (typeof tagged === 'string' && tagged.length > 0) return item;
  const role = resolveTerrainDerivativeRole({ id: item.id, style: item.style } as Layer);
  if (!role) return item;
  return { ...item, style: { ...item.style, terrainDerivative: role } };
}

export function sceneItemToLayer(rawItem: SceneItem, slot: SceneSlot): Layer {
  const item = withTerrainDerivativeRole(rawItem);
  const artifactKind = readArtifactKind(item);
  const unsupportedBinaryVector = isViewerUnsupportedBinaryVectorKind(artifactKind);
  const layerKind = toViewerLayerKind(item.layerKind, slot);
  const contractEndpoint = endpointFromContract(item, slot);
  const workflowCapabilities = rasterWorkflowCapabilities(item, layerKind);
  const pointcloudCapabilities = pointcloudWorkflowCapabilities(item, layerKind);
  const explicitBbox = hasExplicitBbox(item);
  const coordinateMode = inferCoordinateMode(item);

  const endpoints: Layer['endpoints'] = {};
  if (contractEndpoint) {
    assignEndpoint(endpoints, contractEndpoint.contract.endpointKey, contractEndpoint.value, layerKind, item);
    if (contractEndpoint.key !== contractEndpoint.contract.endpointKey) {
      assignEndpoint(endpoints, contractEndpoint.key, contractEndpoint.value, layerKind, item);
    }
    if (contractEndpoint.contract.endpointKey === 'manifest' && contractEndpoint.key !== 'manifest') {
      endpoints.manifest = contractEndpoint.value;
    }
  } else if (slot === 'basemaps') {
    const tiles = endpointHref(item.endpoints.tiles);
    const xyz = endpointHref(item.endpoints.xyz);
    const url = endpointHref(item.endpoints.url);
    const cog = endpointHref(item.endpoints.cog);
    if (tiles) endpoints.tiles = tiles;
    else if (xyz) endpoints.tiles = xyz;
    else if (url) endpoints.tiles = url;
    else if (cog) endpoints.tiles = buildCogTileTemplate(cog, item);
  }

  const isEngineeringModel = slot === 'engineeringModels';

  const layer: Layer = {
    id: item.id,
    tenantId: 'gtwin',
    stacItemId: item.stacItemId ?? item.id,
    kind: layerKind,
    crs: item.crs ?? (coordinateMode === 'georeferenced' ? 'EPSG:4326' : 'LOCAL'),
    bbox: item.bbox ?? [0, 0, 0, 0],
    endpoints,
    style: {
      ...item.style,
      gtwinSlot: slot,
      gtwinLayerKind: item.layerKind,
      ...(item.visualPurpose ? { visualPurpose: item.visualPurpose } : {}),
      ...(item.sceneClass ? { sceneClass: item.sceneClass } : {}),
      ...(item.sceneProfile ? { sceneProfile: item.sceneProfile } : {}),
      ...(typeof item.minCameraDistance === 'number' ? { minCameraDistance: item.minCameraDistance } : {}),
      ...(typeof item.maxCameraDistance === 'number' ? { maxCameraDistance: item.maxCameraDistance } : {}),
      coordinateMode,
      hasExplicitBounds: explicitBbox,
      hasExplicitCrs: Boolean(item.crs),
      ...(contractEndpoint ? { gtwinArtifactKind: contractEndpoint.contract.artifactKind } : {}),
      ...(unsupportedBinaryVector
        ? {
            gtwinArtifactKind: artifactKind,
            viewerUnsupportedReason: VIEWER_UNSUPPORTED_BINARY_VECTOR_MESSAGE,
          }
        : {}),
      ...(isEngineeringModel ? { engineeringPlaceholder: true } : {}),
      ...(workflowCapabilities ? { rasterWorkflowCapabilities: workflowCapabilities } : {}),
      ...(pointcloudCapabilities ? { pointcloudWorkflowCapabilities: pointcloudCapabilities } : {}),
      ...(item.blockTiled ? { blockTiled: true } : {}),
    },
    visibility: {
      ...(typeof item.style?.minZoom === 'number' ? { minZoom: item.style.minZoom } : {}),
      ...(typeof item.style?.maxZoom === 'number' ? { maxZoom: item.style.maxZoom } : {}),
    },
    permissions: {
      roles: [],
      classification: 'tenant',
    },
    label:
      typeof (item as unknown as { title?: unknown }).title === 'string'
        ? (item as unknown as { title: string }).title
        : typeof item.style?.title === 'string'
          ? item.style.title
          : item.id,
    visualPurpose: item.visualPurpose,
    sceneClass: item.sceneClass,
    sceneProfile: item.sceneProfile,
    minCameraDistance: item.minCameraDistance,
    maxCameraDistance: item.maxCameraDistance,
    // RFC-112 CRS fields - plumb through from SceneItem to Layer
    sourceCrs: item.sourceCrs,
    worldFrame: item.worldFrame,
    anchor: item.anchor,
    // V-CRS — standard render-frame passthrough (displayCrs + provenance).
    displayCrs: item.displayCrs,
    displayCrsProj4: item.displayCrsProj4,
    verticalDatum: item.verticalDatum,
    crsProvenanceUrl: item.crsProvenanceUrl,
    alternateOfLayerId: item.alternateOfLayerId,
    alternateLabel: item.alternateLabel,
    alternatePreference: item.alternatePreference,
    derivedFrom: item.derivedFrom,
  };

  // ADR-017 — annotate activation tier from the policy module
  // (`LAYER_COMPOSITION_AND_ACTIVATION.md` §2). The per-item override
  // (`SceneItem.activation`) wins via `ctx.itemOverride`.
  layer.activationTier = resolveActivation(layer, {
    itemOverride: item.activation,
  }).tier;

  // Hillshade/slope/aspect/contours are opt-in companions. Leaving them on
  // by default stacks HSV/viridis overlays on the DEM → salt-and-pepper
  // "static" until the operator hides them in Scene slots.
  const derivativeRole = item.style?.terrainDerivative;
  if (typeof derivativeRole === 'string' && derivativeRole.length > 0) {
    layer.activationTier = 'on-demand';
  }

  return applyStoredGeoreferenceToLayer(layer);
}
